import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import './QuotaBanner.css';

/**
 * Inline banner that warns the user when their daily quota is running out or
 * has already been hit. Fetches `/api/billing/me` once on mount; renders
 * nothing on uncapped plans (usage / enterprise) or when usage is < 80%.
 *
 * Three states:
 *   - "approaching" — 80–99% of the daily cap: amber notice + upgrade CTA
 *   - "at-cap"      — daily cap reached, overage off: red notice + enable overage + upgrade
 *   - "ceiling"     — burst ceiling reached (overage on): red notice + upgrade only
 */
export default function QuotaBanner({ onDismiss }) {
  const [snap, setSnap] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    api.get('/api/billing/me')
      .then((s) => { if (!cancelled) setSnap(s); })
      .catch(() => {}); // silent — not all users may be authed during render
    return () => { cancelled = true; };
  }, []);

  if (dismissed || !snap || !snap.daily_cap) return null;

  const total = snap.today?.total || 0;
  const cap = snap.daily_cap;
  const ceiling = snap.burst_ceiling || cap;
  const pct = total / cap;

  let state = null;
  if (snap.overage_enabled && total >= ceiling) state = 'ceiling';
  else if (!snap.overage_enabled && total >= cap) state = 'at-cap';
  else if (pct >= 0.8) state = 'approaching';
  if (!state) return null;

  const headline = {
    approaching: `You're at ${Math.round(pct * 100)}% of today's limit`,
    'at-cap': 'Daily limit reached',
    ceiling: 'Burst ceiling reached',
  }[state];

  const body = {
    approaching: `${total} of ${cap} events used today. Agents will stop when you hit the cap.`,
    'at-cap': snap.overage_billable
      ? `Agents stopped at ${cap} events. Turn on overage in Settings to keep running, or upgrade your plan.`
      : `Agents stopped at ${cap} events. Upgrade your plan to keep running today.`,
    ceiling: `Agents stopped at ${ceiling} events. Upgrade your plan to handle higher daily volume.`,
  }[state];

  const dismiss = () => {
    setDismissed(true);
    onDismiss?.();
  };

  return (
    <div className={`quota-banner quota-banner-${state}`}>
      <div className="quota-banner-body">
        <span className="quota-banner-headline">{headline}</span>
        <span className="quota-banner-text">{body}</span>
      </div>
      <div className="quota-banner-actions">
        {state === 'at-cap' && snap.overage_billable && (
          <button className="btn btn-sm" onClick={() => navigate('/settings')}>
            Enable overage
          </button>
        )}
        <button className="btn btn-sm btn-primary" onClick={() => navigate('/pricing')}>
          Upgrade
        </button>
        <button
          className="quota-banner-dismiss"
          onClick={dismiss}
          aria-label="Dismiss"
        >×</button>
      </div>
    </div>
  );
}
